<template>
  <TresGroup :visible="isContactRoute">
    <TresMesh
      ref="refUnderlay"
      render-order="10"
    >
      <TresPlaneGeometry :args="[sizes.width.value, sizes.height.value]" />
      <TresMeshBasicMaterial
        color="black"
        :transparent="true"
        :opacity="1"
      />
    </TresMesh>

    <TresGroup ref="refPosition">
      <TresGroup
        ref="refScale"
        :rotation="[MathUtils.degToRad(90), 0, 0]"
      >
        <TresMesh
          :geometry="nodes.world.geometry"
          :material="material"
          :position="[-0.458, 0, -0.099]"
          ref="refWorld"
          render-order="11"
        />

        <DotsMap2Location
          locationId="hongkong"
          :idx="6"
          :position="[0.2674, 0, 0.014]"
        />
        <DotsMap2Location
          locationId="shanghai"
          :idx="7"
          :position="[0.282, 0, -0.014]"
        />

        <DotsMap2Location
          locationId="turku"
          :idx="5"
          :position="[0.035, 0, -0.1202]"
        />
        <DotsMap2Location
          locationId="saint-nazaire"
          :idx="2"
          :position="[-0.0284, 0, -0.0637]"
        />
        <DotsMap2Location
          locationId="paris"
          :idx="1"
          :position="[-0.014, 0, -0.07]"
        />
        <DotsMap2Location
          locationId="emden"
          :idx="4"
          :position="[-0.00025, 0, -0.085]"
        />

        <!-- <DotsMap2Location
          locationId="genoa"
          :idx="3"
          :position="[-0.00025, 0, -0.057]"
        />
        <DotsMap2Location
          locationId="monfalcone"
          :idx="5"
          :position="[0.0278, 0, -0.057]"
        />
        <DotsMap2Location
          locationId="staranzano"
          :idx="5"
          :position="[0.014, 0, -0.057]"
        /> -->

        <DotsMap2Location
          locationId="genoa"
          :idx="3"
          :position="[-0.0143, 0, -0.0498]"
        />
        <DotsMap2Location
          locationId="monfalcone"
          :idx="5"
          :position="[0.0067, 0, -0.0498]"
        />
        <DotsMap2Location
          locationId="staranzano"
          :idx="5"
          :position="[-0.0003, 0, -0.0498]"
        />

        <DotsMap2Location
          locationId="orlando"
          :idx="7"
          :position="[-0.2256, 0, 0.007]"
        />
        <DotsMap2Location
          locationId="miami"
          :idx="0"
          :position="[-0.2115, 0, 0.0205]"
        />
      </TresGroup>
    </TresGroup>
  </TresGroup>
</template>

<script setup lang="ts">
import { useGLTF } from "@tresjs/cientos"
import { MathUtils } from "three"
import dotsParams from "@/components/three/shaders/dotsParams.glsl?raw"
import * as helpers from "@/components/three/shaders/helpers.js"
import * as config from "@/components/three/config/dots-map"
import baseVertex from "@/components/three/shaders/base.vert?raw"

const props = defineProps({})

const emits = defineEmits([])

const { $gsap, $ScrollTrigger } = useNuxtApp()

const { app, debug, browser } = useStores()
const route = useRoute()
const isContactRoute = computed(() => /\/contact(?:-us)?\/?$/.test(route.path))

const { nodes } = await useGLTF("/test.glb", { draco: true })

const refPosition = ref(null)
const refScale = ref(null)
const refUnderlay = ref(null)
const refWorld = ref(null)

const { renderer, sizes } = useTresContext()
const { onLoop } = useRenderLoop()

const rect = useElementRect(".locations-map")

const updateSize = () => {
  if (!refScale.value) return
  refScale.value.scale.setScalar(rect.width * 1.1)
}

watch(() => rect.width, updateSize)
onMounted(updateSize)

onLoop(() => {
  if (!refPosition.value || !refUnderlay.value) return

  refPosition.value.position.y = -rect.top + browser.lvh / 2 - rect.height / 2
  refUnderlay.value.position.y = -rect.top
})

// **********************************************************************
// MATERIAL
// **********************************************************************
const dotsUniforms = await useDotsUniforms()

const uniforms = {
  uFlowMapStrength: { value: 0.3 },

  // AMBIENT
  uAmbientSpeed: { value: config.ambientSpeed },
  uAmbientScale: { value: config.ambientScale },
  uAmbientCutoff: { value: config.ambientCutoff },
  uAmbientStrength: { value: 1 },

  ...dotsUniforms,

  uClick: { value: new Vector2(0.5, 0.5) },
  uClickTime: { value: 0.0 },
  uPulseThickness: { value: 0.02 },
}

// **********************************************************************
// CLICK
// **********************************************************************

// const onClick = function (e) {
//   uniforms.uClickTime.value = uniforms.uTime.value
//   uniforms.uClick.value.x = e.clientX / sizes.width.value
//   uniforms.uClick.value.y = 1.0 - e.clientY / sizes.height.value
//   console.log(uniforms.uClick.value, uniforms.uResolution.value)
// }

// onMounted(() => {
//   window.addEventListener("click", onClick)
// })
// onUnmounted(() => {
//   window.removeEventListener("click", onClick)
// })

const vertexShader = /*glsl*/ `

  ${dotsParams}

  uniform vec2 uClick;
  uniform float uClickTime;
  uniform float uPulseThickness;

  varying float vInPulse;

  ${helpers.worldToNdc}
  ${helpers.resizeUVContain}
  ${helpers.scaleUV}
  ${helpers.translateUV}
  ${helpers.getTranslateMatrix}
  ${helpers.getScaleMatrix}


  
  void main() {

    vec4 transformed = vec4(position, 1.0);

    // SCREEN UV
    // vec3 vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    // vec3 ndcSpacePos = worldToNdc(vWorldPos.xyz, projectionMatrix, viewMatrix, uCameraNear, uCameraFar);
    // vec2 screenUv = (ndcSpacePos.xy + 1.0) * 0.5;
    // float screenAspect = uResolution.x / uResolution.y;

    // // PULSE
    
    // vec2 center = screenUv - uClick;
    // center.x *= screenAspect;
    // float dist = length(center);
    // float wave = dist - (uTime - uClickTime) * 0.1;
    
    // float falloff = smoothstep(0.0, uPulseThickness, wave) * smoothstep(uPulseThickness * 2.0, uPulseThickness, wave);
    // float timeFade = 1.0 - smoothstep(0.0, 2.0, uTime - uClickTime);
    
    // float pulse = falloff * timeFade;
    // vInPulse = step(1.0 - pulse, 0.5);

    // mat4 tPos = mat4(vec4(1.0,0.0,0.0,0.0),
    //       vec4(0.0,1.0,0.0,0.0),
    //       vec4(0.0,0.0,1.0,0.0),
    //       vec4(0.0, 0.0, vInPulse * 0.01 ,1.0));

    // transformed = tPos * transformed;


    gl_Position = projectionMatrix * modelViewMatrix * transformed;
    vUv = uv;
  }
`

const fragmentShader = /*glsl*/ `

  ${dotsParams}

  uniform vec2 uClick;
  uniform float uClickTime;
  uniform float uPulseThickness;

  varying float vInPulse;
  
  void main() {

    vec2 screenUv = gl_FragCoord.xy / uResolution.xy;
    float screenAspect = uResolution.x / uResolution.y;
    vec3 color = vec3(0.5);

    // GRADIENT
    // vec4 gradient = texture2D(tGradient, screenUv);
    // color *= gradient.rgb;


    // MASK
    float mask = 0.;

    // NOISE
    vec2 nuv = vec2(screenUv.x, screenUv.y);
    vec2 uvNoise1 = vec2(nuv.x + uTime * uAmbientSpeed, nuv.y) * (1.0 / uAmbientScale);
    vec2 uvNoise2  = vec2(nuv.x - uTime * uAmbientSpeed, nuv.y) * (1.0 / uAmbientScale);
    vec4 noise1 = texture2D(tNoise, uvNoise1);
    vec4 noise2 = texture2D(tNoise, uvNoise2);
    mask += (smoothstep(0.5, 1.0, noise1.r) + smoothstep(uAmbientCutoff, 1.0, noise2.g)) ;

    // FLOW MAP
    vec4 flowMap = texture2D(tFlowMap, screenUv);
    mask += smoothstep(0.5, 1.0, flowMap.b) * 1.0 * uFlowMapStrength;

    // // PULSE
    // vec2 center = screenUv - uClick;
    // center.x *= screenAspect;
    // float dist = length(center);
    // float wave = dist - (uTime - uClickTime) * 0.5;
    
    // float falloff = smoothstep(0.0, uPulseThickness, wave) * smoothstep(uPulseThickness * 2.0, uPulseThickness, wave);
    // float timeFade = 1.0 - smoothstep(0.0, 2.0, uTime - uClickTime);
    
    // // // Combine for final pulse effect
    // float pulse = falloff * timeFade;
    // // mask += pulse;
    // // mask += vInPulse;

    // COLOR
    color *= clamp(mask, 0.0, 1.0); // mask

    gl_FragColor = vec4(color, 1.0);

    
  }
`

const material = new ShaderMaterial({
  uniforms,
  // vertexShader: baseVertex,
  vertexShader,
  fragmentShader,
  toneMapped: false,
  transparent: true,
})
</script>

<style lang="scss" scoped>
@import "@/styles/shared.scss";
</style>
